/**
 * Sidebar Navigation
 * 
 * Collapsible sidebar with icon-based nav links.
 * Uses NavLink for automatic active state.
 * On mobile (<768px) it's rendered inside a Sheet.
 */

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Settings,
  Users,
  LifeBuoy,
  Shield,
  Rocket,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Link2,
  Layers,
  ShieldCheck,
  Library,
  FileCheck2,
  Wallet,
  History,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { isClerkConfigured } from '@/config';
import { useCurrentUser } from '@/auth';
import { useApiQuery } from '@/api/useApiQuery';
import type { MetaPortfolioDto } from '@/pages/MetaPortfoliosPage';
import { isMandateUiEnabled } from '@/mandate/enabled';
import { trackSidebarNavClicked } from '@/lib/analytics';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  end?: boolean; // exact match
  external?: boolean; // open as external link
  disabled?: boolean; // shown as a greyed-out line, not a link
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

export interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

// ---------------------------------------------------------------------------
// Navigation config
// ---------------------------------------------------------------------------

/** Label of the disabled "live trading" line (exported for tests). */
export const LIVE_TRADING_UNAVAILABLE_LABEL = 'Live trading: not available yet';

export interface NavState {
  isAdmin: boolean;
  /** Tenant has at least one active meta-portfolio (retired surface, R48-D1). */
  hasActiveMetaPortfolio: boolean;
  /** Tenant has more than one member, or at least one invitation. */
  showTeam: boolean;
  /** Mandate UI enabled (Limits, Proposals, Your plan, The rules). */
  showMandate: boolean;
}

/**
 * Sidebar order follows the customer's journey (design audit R58-D5, PR group
 * 6): Home, Broker accounts, Limits, Proposals, Your plan, Record (R70 D1,
 * the account history page), then a Reference
 * group (The rules, Docs), then Account. Plain order, no numbers (the
 * numbered checklist lives on Home). Labels only: every route is unchanged.
 *
 * Hidden unless the tenant needs them:
 *  - Portfolio (retired meta-portfolios): only with an active meta-portfolio.
 *  - Team: only with more than one member or an invitation.
 * Live trading is not its own mid-flow item: it is a disabled line saying it
 * is not available yet (the /dashboard/deploy route still works).
 */
export function getNavGroups({ isAdmin, hasActiveMetaPortfolio, showTeam, showMandate }: NavState): NavGroup[] {
  const saas = isClerkConfigured();

  const main: NavItem[] = [
    { label: 'Home', path: '/dashboard', icon: LayoutDashboard, end: true },
    { label: 'Broker accounts', path: '/dashboard/exchanges', icon: Link2 },
  ];
  if (showMandate) {
    main.push(
      { label: 'Limits', path: '/dashboard/mandate', icon: ShieldCheck },
      { label: 'Proposals', path: '/dashboard/proposals', icon: FileCheck2 },
      { label: 'Your plan', path: '/dashboard/account', icon: Wallet },
      { label: 'Record', path: '/dashboard/record', icon: History },
    );
  }
  if (saas) {
    main.push({
      label: LIVE_TRADING_UNAVAILABLE_LABEL,
      path: '/dashboard/deploy',
      icon: Rocket,
      disabled: true,
    });
  }
  if (hasActiveMetaPortfolio) {
    main.push({ label: 'Portfolio', path: '/dashboard/meta-portfolios', icon: Layers });
  }

  const reference: NavItem[] = [];
  if (showMandate) {
    reference.push({ label: 'The rules', path: '/dashboard/library', icon: Library });
  }
  reference.push({ label: 'Docs', path: '/dashboard/docs', icon: BookOpen });

  const account: NavItem[] = [{ label: 'Settings', path: '/dashboard/settings', icon: Settings }];
  if (saas) {
    if (showTeam) account.push({ label: 'Team', path: '/dashboard/team', icon: Users });
    account.push(
      { label: 'Billing', path: '/dashboard/billing', icon: CreditCard },
      { label: 'Support', path: '/dashboard/support', icon: LifeBuoy },
    );
  }

  const groups: NavGroup[] = [
    { title: 'Main', items: main },
    { title: 'Reference', items: reference },
    { title: 'Account', items: account },
  ];
  if (saas && isAdmin) {
    groups.push({ title: 'Admin', items: [{ label: 'Admin', path: '/dashboard/admin', icon: Shield }] });
  }
  return groups;
}

// ---------------------------------------------------------------------------
// Nav link item
// ---------------------------------------------------------------------------

const SidebarLink: React.FC<{
  item: NavItem;
  collapsed: boolean;
  group: string;
}> = ({ item, collapsed, group }) => {
  const Icon = item.icon;
  const location = useLocation();

  const linkClasses = cn(
    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
    'hover:bg-accent hover:text-accent-foreground',
    'text-muted-foreground',
    collapsed && 'justify-center px-2',
  );

  const inner = (
    <>
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </>
  );

  // Click telemetry for every sidebar item (design council PR A, 2026-10-14)
  // -- covers the whole sidebar going forward, not just the items this PR
  // touches, so future "is X still used" questions have real data instead of
  // a guess.
  const handleClick = () => {
    trackSidebarNavClicked({ label: item.label, path: item.path, group });
  };

  // Compute active state manually to handle query-string paths (e.g. /settings vs /billing)
  const [itemPathname, itemSearch] = item.path.split('?').map((p, i) => (i === 1 ? `?${p}` : p));
  const hasQuery = item.path.includes('?');
  const manualIsActive = hasQuery
    ? location.pathname === itemPathname && location.search === itemSearch
    : location.pathname === item.path && location.search === '';

  if (item.disabled) {
    const disabledLine = (
      <div
        aria-disabled="true"
        tabIndex={collapsed ? 0 : undefined}
        className={cn(
          'flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground/60',
          collapsed && 'justify-center px-2',
        )}
      >
        {inner}
      </div>
    );
    if (collapsed) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{disabledLine}</TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            {item.label}
          </TooltipContent>
        </Tooltip>
      );
    }
    return disabledLine;
  }

  const link = item.external ? (
    <a href={item.path} target="_blank" rel="noopener noreferrer" className={linkClasses} onClick={handleClick}>
      {inner}
    </a>
  ) : (
    <NavLink
      to={item.path}
      end={item.end ?? true}
      onClick={handleClick}
      className={() =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          manualIsActive
            ? 'bg-primary/10 text-primary border-l-2 border-primary'
            : 'text-muted-foreground',
          collapsed && 'justify-center px-2',
        )
      }
    >
      {inner}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return link;
};

// ---------------------------------------------------------------------------
// Sidebar component
// ---------------------------------------------------------------------------

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const { role } = useCurrentUser();
  const isAdmin = role === 'admin' || role === 'owner';
  const saas = isClerkConfigured();

  // Portfolio (retired meta-portfolios) is shown only to tenants that still
  // have an active one. Unknown/loading/error -> hidden.
  const { data: metaPortfolios } = useApiQuery<MetaPortfolioDto[]>(
    ['meta-portfolios', 'sidebar'],
    '/api/meta-portfolios',
  );
  const hasActiveMetaPortfolio = Array.isArray(metaPortfolios) && metaPortfolios.some((p) => p.is_active);

  // Team is shown only once there is someone to manage: more than one member
  // or an invitation exists.
  const { data: teamMembers } = useApiQuery<{ members: unknown[] }>(
    ['team', 'sidebar', 'members'],
    '/api/team/members',
    { enabled: saas },
  );
  const { data: teamInvitations } = useApiQuery<{ invitations: unknown[] }>(
    ['team', 'sidebar', 'invitations'],
    '/api/team/invitations',
    { enabled: saas },
  );
  const showTeam =
    (teamMembers?.members?.length ?? 0) > 1 || (teamInvitations?.invitations?.length ?? 0) > 0;

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r bg-card transition-[width] duration-200',
        collapsed ? 'w-[60px]' : 'w-[240px]',
      )}
    >
      {/* Brand */}
      <div
        className={cn(
          'flex h-14 items-center border-b px-4',
          collapsed ? 'justify-center' : 'gap-3',
        )}
      >
        <span className="text-xl">📊</span>
        {!collapsed && (
          <span className="text-base font-semibold text-foreground">
            Mendl Labs
          </span>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-2">
        <nav className="flex flex-col gap-1 px-2">
          {getNavGroups({
            isAdmin,
            hasActiveMetaPortfolio,
            showTeam,
            showMandate: isMandateUiEnabled(),
          }).map((group, gi) => (
            <React.Fragment key={group.title}>
              {gi > 0 && <Separator className="my-2" />}
              {!collapsed && (
                <span className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                  {group.title}
                </span>
              )}
              {group.items.map((item) => (
                <SidebarLink
                  key={item.path}
                  item={item}
                  collapsed={collapsed}
                  group={group.title}
                />
              ))}
            </React.Fragment>
          ))}
        </nav>
      </ScrollArea>

      {/* Collapse toggle */}
      <div className="border-t p-2">
        <Button
          variant="ghost"
          size="icon"
          className="w-full"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
